import { IonIcon } from "@ionic/react";
import { chevronBackOutline, chevronForwardOutline } from "ionicons/icons";
import React, { useMemo, useRef, useState } from "react";
import { EffectCards, Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide, type SwiperClass } from "swiper/react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { TicketCard } from "./TicketCard";
import type { TicketViewerItem } from "./ticketViewer.tools";

/**
 * Props of {@link TicketViewerDeck}.
 */
export interface TicketViewerDeckProps {
    "items":        readonly TicketViewerItem[];
    "initialIndex": number;

    /** Backend provider id shared by the booking (`purchase.provider`). */
    "provider":         string;
    "itineraryId":      string;
    "initialSegmentId": string | null;
    "isDarkUi":         boolean;

    /** Index of the card currently showing its back face — `null` when none. */
    "flippedIndex": number | null;

    /** Marks a card as flipped — the deck also slides it to the front. */
    "onFlipCard": ( index: number ) => void;

    /** Returns the flipped card to its front face. */
    "onUnflipCard": () => void;

    /** Opens the refund panel for the given provider ticket id. */
    "onRequestRefund"?: ( providerTicketId: string ) => void;
}

/**
 * Warm paper surface of the light-scheme viewer body: keeps the modal light
 * while letting the (always light) ticket cards stand out. Covers the whole
 * content area below the header — margins and dots row included.
 */
const DECK_SURFACE_LIGHT = "#F0EBE0";

/**
 * Wheel handling: the first delta past the noise floor flips one card, then
 * the lock swallows the rest of the gesture — one notch is always one card,
 * whatever the driver reports per notch (10 px on some mice, 100 on others).
 */
const WHEEL_MIN_DELTA = 4;
const WHEEL_SLIDE_LOCK_MS = 250;

/**
 * Stacked ticket deck: native swipe on touch, arrows/dots/keyboard/mouse-wheel
 * on desktop — mouse drag is deliberately not a navigation gesture.
 *
 * @remarks Remounts on `(itineraryId, initialSegmentId)` change via `key` so a
 * reopen always lands deterministically on the requested ticket.
 * @param props - Component props.
 * @returns The deck with its navigation chrome.
 */
export const TicketViewerDeck: React.FC<TicketViewerDeckProps> = ( {
    items,
    initialIndex,
    provider,
    itineraryId,
    initialSegmentId,
    isDarkUi,
    flippedIndex,
    onFlipCard,
    onUnflipCard,
    onRequestRefund,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();
    const swiperRef = useRef<SwiperClass | null>( null );
    const wheelLockUntilRef = useRef( 0 );
    const [ activeIndex, setActiveIndex ] = useState( initialIndex );

    const isBeginning = activeIndex <= 0;
    const isEnd = activeIndex >= items.length - 1;

    /*
     * One surface for the whole viewer body — dark: the lifted navy tray is
     * stretched over the full content area so only the header keeps the darker
     * modal tone; light: warm paper.
     */
    const deckSurface = isDarkUi ? palette.overlayCardBackground : DECK_SURFACE_LIGHT;

    /*
     * Native wheel handling over the whole deck body (Swiper's own Mousewheel
     * module only listens on the `.swiper` element — the arrows and margins
     * would dead-end the gesture).
     */
    const handleWheel = ( event: React.WheelEvent<HTMLDivElement> ): void => {
        // Inside a flipped card's scrollable text, the wheel reads — not navigates.
        if ( ( event.target as HTMLElement ).closest( "[data-card-scroll]" ) !== null ) {
            return;
        }

        const delta = Math.abs( event.deltaX ) > Math.abs( event.deltaY ) ? event.deltaX : event.deltaY;
        const now = Date.now();

        if ( Math.abs( delta ) < WHEEL_MIN_DELTA || now < wheelLockUntilRef.current ) {
            return;
        }

        wheelLockUntilRef.current = now + WHEEL_SLIDE_LOCK_MS;

        if ( delta > 0 ) {
            swiperRef.current?.slideNext();
        } else {
            swiperRef.current?.slidePrev();
        }
    };

    /*
     * Background-card hits are resolved by geometry, not by which element
     * receives the pointer event: the cards effect stacks full-width slide
     * boxes in a `preserve-3d` context (`translateZ`, `rotateZ`) where
     * `elementFromPoint` never returns a background card's subtree — the hit
     * element under the cursor is not usable. `getBoundingClientRect`
     * reflects the real transformed position, so the visible strip of a
     * background card always resolves to the card it belongs to.
     */
    const resolveBackgroundCardIndex = (
        container: HTMLElement,
        clientX: number,
        clientY: number,
    ): number | null => {
        const isInside = ( element: Element | null ): boolean => {
            if ( element === null ) {
                return false;
            }

            const rect = element.getBoundingClientRect();

            return clientX >= rect.left && clientX <= rect.right &&
              clientY >= rect.top && clientY <= rect.bottom;
        };

        // A point inside the front card's own box is not a navigation target.
        if ( isInside( container.querySelector( `[data-card-hitbox="${ activeIndex }"]` ) ) ) {
            return null;
        }

        let targetIndex: number | null = null;

        container.querySelectorAll<HTMLElement>( "[data-card-hitbox]" ).
            forEach( ( element ) => {
                const index = Number( element.dataset["cardHitbox"] );

                if ( index === activeIndex || !isInside( element ) ) {
                    return;
                }

                // Cards fan out with distance from the active one — nearest wins.
                if ( targetIndex === null ||
                  Math.abs( index - activeIndex ) < Math.abs( targetIndex - activeIndex ) ) {
                    targetIndex = index;
                }
            } );

        return targetIndex;
    };

    const handleDeckClick = ( event: React.MouseEvent<HTMLElement> ): void => {
        // Buttons (arrows, dots, per-card info) own their clicks.
        if ( ( event.target as HTMLElement ).closest( "button" ) !== null ) {
            return;
        }

        const targetIndex = resolveBackgroundCardIndex(
            event.currentTarget,
            event.clientX,
            event.clientY,
        );

        if ( targetIndex !== null ) {
            swiperRef.current?.slideTo( targetIndex );
        }
    };

    /*
     * The pointer cursor cannot come from the hovered element (same
     * hit-testing limit as clicks) — it is driven by the same geometric
     * resolution so the hand only shows where a click would navigate.
     */
    const handleDeckMouseMove = ( event: React.MouseEvent<HTMLElement> ): void => {
        const swiper = swiperRef.current;

        if ( swiper === null || swiper.destroyed ) {
            return;
        }

        const hoverable = ( event.target as HTMLElement ).closest( "button" ) === null &&
          resolveBackgroundCardIndex( event.currentTarget, event.clientX, event.clientY ) !== null;

        swiper.el.style.cursor = hoverable ? "pointer" : "";
    };

    const handleDeckMouseLeave = (): void => {
        const swiper = swiperRef.current;

        if ( swiper !== null && !swiper.destroyed ) {
            swiper.el.style.cursor = "";
        }
    };

    /*
     * `relative z-10` keeps the arrows clickable: the cards effect unclips the
     * slides (`overflow: visible`), so their invisible protrusions otherwise
     * sit on top of the buttons. `.swiper` itself is z-index 1.
     */
    const arrowButtonClass = "relative z-10 hidden h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-solid backdrop-blur-sm transition-transform duration-150 ease-out enabled:active:scale-95 enabled:hover:scale-105 focus-visible:outline-none focus-visible:ring-2 disabled:cursor-default disabled:opacity-30 md:flex";
    const arrowButtonStyle = {
        "borderColor": palette.overlayBorderStrong,
        "background":  isDarkUi ? palette.overlayCardBackgroundStrong : "rgba(255, 255, 255, 0.9)",
        "color":       palette.overlayTextPrimary,
    };

    return (
        <div
            className="flex h-full min-h-0 flex-col"
            onClick={handleDeckClick}
            onMouseLeave={handleDeckMouseLeave}
            onMouseMove={handleDeckMouseMove}
            onWheel={handleWheel}
            style={{ "background": deckSurface }}
        >
            <div className="mx-2 flex min-h-0 flex-1 items-center gap-1 rounded-2xl px-1 md:mx-3 md:px-2">
                <button
                    aria-label="Billet précédent"
                    className={arrowButtonClass}
                    data-testid="ticket-deck-prev"
                    disabled={isBeginning}
                    onClick={() => swiperRef.current?.slidePrev()}
                    style={arrowButtonStyle}
                    type="button"
                >
                    <IonIcon
                        aria-hidden={true}
                        className="h-5 w-5"
                        icon={chevronBackOutline}
                    />
                </button>
                <Swiper
                    aria-label="Billets de l'itinéraire"
                    aria-roledescription="carousel"
                    cardsEffect={{
                        "perSlideOffset": 12,
                        "perSlideRotate": 3,
                        "slideShadows":   false,
                    }}
                    className="h-full min-h-0 min-w-0 flex-1"
                    effect="cards"
                    grabCursor={false}
                    initialSlide={initialIndex}
                    key={`${ itineraryId }:${ initialSegmentId ?? "all" }`}
                    keyboard={{
                        "enabled":        true,
                        "onlyInViewport": false,
                    }}
                    modules={[ EffectCards, Keyboard ]}
                    onSlideChange={( swiper ) => {
                        setActiveIndex( swiper.activeIndex );

                        // A flipped card returns to its front face when it leaves the front.
                        if ( flippedIndex !== null && flippedIndex !== swiper.activeIndex ) {
                            onUnflipCard();
                        }
                    }}
                    onSwiper={( swiper ) => {
                        swiperRef.current = swiper;
                    }}
                    preventClicks={false}
                    preventClicksPropagation={false}
                    role="region"
                    simulateTouch={false}
                    slidesPerView={1}
                    speed={reducedMotion ? 150 : 350}
                >
                    {items.map( ( item, index ) => (
                        <SwiperSlide
                            aria-label={`Billet ${ index + 1 } sur ${ items.length }`}
                            aria-roledescription="slide"
                            className="h-full"
                            key={item.key}
                        >
                            {/*
                             * `data-card-hitbox` anchors the card's real box:
                             * the deck-level handler resolves background-card
                             * clicks geometrically because 3D-stacked slide
                             * boxes make element hit-testing unreliable.
                             */}
                            <div
                                className="mx-auto flex h-full w-full max-w-[320px] items-center justify-center pb-6 pt-3 md:max-w-[380px]"
                                data-card-hitbox={index}
                            >
                                <TicketCard
                                    flipped={index === flippedIndex}
                                    item={item}
                                    onActivate={index === activeIndex ? undefined : () => swiperRef.current?.slideTo( index )}
                                    onFlip={() => {
                                        swiperRef.current?.slideTo( index );
                                        onFlipCard( index );
                                    }}
                                    onFlipBack={onUnflipCard}
                                    onRequestRefund={onRequestRefund === undefined ? undefined : () => onRequestRefund( item.ticket.providerTicketId )}
                                    provider={provider}
                                />
                            </div>
                        </SwiperSlide>
                    ) )}
                </Swiper>
                <button
                    aria-label="Billet suivant"
                    className={arrowButtonClass}
                    data-testid="ticket-deck-next"
                    disabled={isEnd}
                    onClick={() => swiperRef.current?.slideNext()}
                    style={arrowButtonStyle}
                    type="button"
                >
                    <IonIcon
                        aria-hidden={true}
                        className="h-5 w-5"
                        icon={chevronForwardOutline}
                    />
                </button>
            </div>
            <div className="flex shrink-0 items-center justify-center gap-2 pb-3 pt-1">
                {items.map( ( item, index ) => (
                    <button
                        aria-current={index === activeIndex ? "true" : undefined}
                        aria-label={`Billet ${ index + 1 }`}
                        className="h-2 cursor-pointer rounded-full transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2"
                        data-testid={`ticket-deck-dot-${ index }`}
                        key={item.key}
                        onClick={() => swiperRef.current?.slideTo( index )}
                        style={{
                            "background": index === activeIndex ? palette.overlayTextPrimary : palette.overlayBorderStrong,
                            "width":      index === activeIndex ? "1.25rem" : "0.5rem",
                        }}
                        type="button"
                    />
                ) )}
            </div>
        </div>
    );
};
